/*----------------------------------
- PERSONA AUTH CTA PAIR
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import {
    buildLandingPersonaSignupHref,
    type LandingSignupPersona,
} from '@/client/catalogs/landing/publicLinks';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';

/*----------------------------------
- TYPES
----------------------------------*/

type PersonaAuthCtaPairProps = {
    attribution: string;
    founderTriggerId: ConversionTriggerId;
    investorTriggerId: ConversionTriggerId;
    page: string;
    section: string;
    size?: 'lg' | 'md';
    surface: string;
    tone?: 'dark' | 'light';
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PersonaAuthCtaPair = ({
    attribution,
    founderTriggerId,
    investorTriggerId,
    page,
    section,
    size = 'lg',
    surface,
    tone = 'light',
}: PersonaAuthCtaPairProps) => {
    const investorHref = buildLandingPersonaSignupHref('investor', attribution);
    const founderHref = buildLandingPersonaSignupHref('founder', attribution);
    const trackEntry = (
        persona: LandingSignupPersona,
        label: string,
        href: string,
        triggerId: ConversionTriggerId,
    ) => {
        trackLandingCta({ page, section, label, kind: 'persona', href, surface });
        trackConversionTrigger(triggerId, {
            href,
            page,
            section,
            context: { persona },
        });
    };

    return (
        <>
            <CtaPill
                href={investorHref}
                variant={tone === 'dark' ? 'accent-inverted' : 'accent'}
                size={size}
                data-testid="persona-auth-cta"
                data-persona="investor"
                data-attribution={attribution}
                onClick={() => trackEntry('investor', "I'm an Investor", investorHref, investorTriggerId)}
            >
                I'm an Investor
            </CtaPill>
            <CtaPill
                href={founderHref}
                variant="light"
                size={size}
                data-testid="persona-auth-cta"
                data-persona="founder"
                data-attribution={attribution}
                onClick={() => trackEntry('founder', "I'm a Founder", founderHref, founderTriggerId)}
            >
                I'm a Founder
            </CtaPill>
        </>
    );
};

export default PersonaAuthCtaPair;
